/**
 * 发品 / 部分编辑（partialEdit）确认前平台侧预检：
 * 1) 商品发布规范 query-publish-fill-in-standard
 * 2) 关联属性填写规则 get-associated-attribute-rules
 * 3) 商家 SKU 唯一性 check-supplierSku-repeated
 *
 * 文档说明：partialEdit 入参可简化，但平台仍会补齐字段后做完整发布校验，
 * 因此编辑提交同样走本预检。
 */

import { sheinPlatformAPI } from '@/utils/api';
import {
  collectProductAttributePairs,
  validateAssociatedRulesRaw,
} from '@/shein-platform/utils/sheinAssociatedAttributeRules';
import {
  parseFillInStandardMap,
  resolveSkuPackageCapabilities,
} from '@/shein-platform/utils/sheinPublishFillInStandard';
import { validateShopPublishCurrency } from '@/shein-platform/utils/resolveSheinPublishCurrency';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import { extractSiteListCurrencies } from '@/shein-platform/utils/sheinSiteList';

export { parseFillInStandardMap };

function fail(message, section = 'basic', field = 'form') {
  return { ok: false, message, section, field };
}

function unwrapInfo(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  if (raw.info != null) return raw.info;
  return raw;
}

function skuHasQuantity(sku) {
  return String(sku?.quantity ?? '').trim() !== '';
}

function skuHasPackageType(sku) {
  const pt = sku?.packageType;
  return pt !== '' && pt != null && String(pt) !== '0';
}

function collectSupplierSkuRows(form = {}) {
  const rows = [];
  (form.skcList || []).forEach((skc, skcIndex) => {
    (skc?.skuRows || []).forEach((sku, skuIndex) => {
      const supplierSku = String(sku?.supplierSku || '').trim();
      if (!supplierSku) return;
      const originalSupplierSku = String(
        sku?.originalSupplierSku || sku?.original_supplier_sku || '',
      ).trim();
      rows.push({
        supplierSku,
        originalSupplierSku,
        skuCode: String(sku?.skuCode || sku?.platformSku || '').trim(),
        skcIndex,
        skuIndex,
        colorLabel: skc?.colorLabel || skc?.colorExtraValue || `规格${skcIndex + 1}`,
        sizeLabel: sku?.sizeLabel || sku?.sizeValueId || `SKU${skuIndex + 1}`,
      });
    });
  });
  return rows;
}

/**
 * 本地：商家 SKU 必填、长度、表单内重复
 */
export function validateSupplierSkusLocal(form = {}) {
  const rows = collectSupplierSkuRows(form);
  if (!rows.length) {
    return fail('请为每个 SKU 填写商家 SKU（supplier_sku）', 'spec', 'supply');
  }
  const seen = new Map();
  for (const row of rows) {
    if (row.supplierSku.length > 200) {
      return fail(
        `「${row.colorLabel} / ${row.sizeLabel}」商家 SKU 不能超过 200 个字符`,
        'spec',
        'supply',
      );
    }
    if (!row.supplierSku.includes('-')) {
      return fail(
        `「${row.colorLabel} / ${row.sizeLabel}」商家 SKU 格式要求：须含短横线「-」（填写时以 - 结尾，保存时自动拼接印花图名称）`,
        'spec',
        'supply',
      );
    }
    const key = row.supplierSku.toLowerCase();
    if (seen.has(key)) {
      const prev = seen.get(key);
      return fail(
        `商家 SKU「${row.supplierSku}」在表单内重复（${prev.colorLabel}/${prev.sizeLabel} 与 ${row.colorLabel}/${row.sizeLabel}），店内须唯一`,
        'spec',
        'supply',
      );
    }
    seen.set(key, row);
  }
  // 有 SKU 行但漏填
  for (const skc of form.skcList || []) {
    for (const sku of skc?.skuRows || []) {
      if (!String(sku?.supplierSku || '').trim()) {
        const color = skc?.colorLabel || skc?.colorExtraValue || '规格';
        const size = sku?.sizeLabel || sku?.sizeValueId || 'SKU';
        return fail(`请填写「${color} / ${size}」商家 SKU`, 'spec', 'supply');
      }
    }
  }
  return { ok: true };
}

function validateAgainstFillInStandard(form, standardRaw, attrTpl) {
  const map = parseFillInStandardMap(standardRaw);
  const info = unwrapInfo(standardRaw) || {};

  const brandRule = map.get('brand_code');
  if (brandRule?.required && brandRule.show && !String(form.brandCode || '').trim()) {
    return fail('商品发布规范要求必填品牌，请选择品牌', 'basic', 'brandCode');
  }

  const defaultLang = String(
    info.default_language
    || info.defaultLanguage
    || map.get('default_language')?.raw?.default_language
    || '',
  ).trim();
  if (defaultLang && form.titleLanguage && String(form.titleLanguage) !== defaultLang) {
    // 不强拦语种不一致（用户可能多语种标题），但标题最大长度按规范
  }

  const maxLen = Number(
    info.default_language_title_max_length
    ?? info.defaultLanguageTitleMaxLength
    ?? 0,
  );
  const title = String(form.title || '');
  if (maxLen > 0 && title.length > maxLen) {
    return fail(`产品标题超过发布规范上限（${maxLen} 字符，当前 ${title.length}）`, 'basic', 'title');
  }

  // 规范里声明必填、且我们表单有对应字段的其它项
  const skcTitle = map.get('skc_title');
  if (skcTitle?.required && skcTitle.show) {
    const missing = (form.skcList || []).some((skc) => !String(skc?.skcTitle || skc?.title || '').trim());
    // 当前编辑器多数场景不单独填 skc_title，仅在有字段时校验
    if (missing && (form.skcList || []).some((skc) => 'skcTitle' in (skc || {}))) {
      return fail('商品发布规范要求填写 SKC 标题', 'spec', 'skcList');
    }
  }

  // 建议零售价（suggest_price）必填时不再在此拦截：组包会按
  // 「表单填写 → SKU 建议零售价 → 供货价」回退，避免批次刊登无 UI 可填而整单失败。

  const caps = resolveSkuPackageCapabilities(standardRaw);
  const skus = (form.skcList || []).flatMap((skc) => (
    (Array.isArray(skc?.skuRows) ? skc.skuRows : []).map((sku) => ({ skc, sku }))
  ));

  if (!caps.quantityInfo.show) {
    const dirty = skus.some(({ sku }) => skuHasQuantity(sku));
    if (dirty) {
      return fail('当前类目不支持 SKU 维度件数，请清空包装件数后再提交', 'size', 'supply');
    }
  } else if (caps.quantityInfo.required) {
    const missing = skus.some(({ sku }) => !skuHasQuantity(sku));
    if (missing) {
      return fail('商品发布规范要求填写 SKU 包装件数', 'size', 'supply');
    }
  }

  if (!caps.packageType.show) {
    const dirty = skus.some(({ sku }) => skuHasPackageType(sku));
    if (dirty) {
      return fail('当前类目不支持 SKU 包装类型，请清空后再提交', 'size', 'supply');
    }
  } else if (caps.packageType.required) {
    const missing = skus.some(({ sku }) => !skuHasPackageType(sku));
    if (missing) {
      return fail('商品发布规范要求选择 SKU 包装类型', 'size', 'supply');
    }
  }

  void attrTpl;
  return { ok: true, standardMap: map, standardInfo: info, skuPkgCaps: caps };
}

/**
 * @param {object} opts
 * @param {string|number} opts.shopId
 * @param {object} opts.form
 * @param {boolean} [opts.isEdit]
 * @param {object|null} [opts.attrTpl]
 * @param {string} [opts.shopType]
 * @param {object|null} [opts.shopCaps]
 * @param {object|null} [opts.siteList]
 * @param {Map|null} [opts.standardCache] 同店同类目发品规范缓存（批量核验复用）
 */
export async function runSheinPublishPreflight({
  shopId,
  form,
  isEdit = false,
  attrTpl = null,
  shopType = '',
  shopCaps = null,
  siteList = null,
  standardCache = null,
}) {
  const localSku = validateSupplierSkusLocal(form);
  if (!localSku.ok) return localSku;

  const categoryId = Number(form.categoryId);
  const cacheKey = `${shopId}:${Number.isFinite(categoryId) ? categoryId : ''}`;
  let standardRaw = null;
  try {
    if (standardCache?.has(cacheKey)) {
      standardRaw = await standardCache.get(cacheKey);
    } else {
      const pending = sheinPlatformAPI.getPublishFillInStandard(shopId, {
        category_id: Number.isFinite(categoryId) ? categoryId : undefined,
      });
      if (standardCache) {
        standardCache.set(cacheKey, pending);
        pending.catch(() => { standardCache.delete(cacheKey); });
      }
      standardRaw = await pending;
    }
  } catch (ex) {
    return fail(ex?.message || '查询商品发布规范失败，请稍后重试', 'basic', 'form');
  }
  const std = validateAgainstFillInStandard(form, standardRaw, attrTpl);
  if (!std.ok) return std;

  const caps = shopCaps || sheinShopCapabilities(shopType || form?.shopType);
  let resolvedSiteList = siteList || form?.siteList || null;
  if (caps.usesPriceInfo && !extractSiteListCurrencies(resolvedSiteList).length && shopId) {
    try {
      const siteRes = await sheinPlatformAPI.getShopSiteList(shopId, { refresh: true });
      resolvedSiteList = siteRes?.siteList ?? resolvedSiteList;
    } catch {
      // 保留快照，由币种校验给出可读错误
    }
  }
  const currencyCheck = validateShopPublishCurrency(form, {
    shopCaps: caps,
    shopType: shopType || form?.shopType,
    siteList: resolvedSiteList,
    standardRaw,
  });
  if (!currencyCheck.ok) return currencyCheck;

  const productTypeId = Number(form.productTypeId);
  const attributeList = collectProductAttributePairs(form);
  const needLinkedRules = Number.isFinite(categoryId)
    && Number.isFinite(productTypeId)
    && attributeList.length > 0;

  const skuRows = collectSupplierSkuRows(form);
  const uniqueSkus = [...new Set(skuRows.map((r) => r.supplierSku))];
  // 编辑：仅跳过「仍为本平台 SKU 原商家编码」的项（改码后仍走平台查重）
  const selfOwnedOriginal = new Set(
    skuRows
      .filter((r) => r.skuCode && r.originalSupplierSku)
      .map((r) => r.originalSupplierSku.toLowerCase()),
  );
  const toCheck = isEdit
    ? uniqueSkus.filter((s) => {
      const key = s.toLowerCase();
      // 未改码且仍等于原编码 → 平台必然 repeated=true，跳过
      if (selfOwnedOriginal.has(key)) {
        const stillSelf = skuRows.some(
          (r) => r.skuCode
            && r.originalSupplierSku
            && r.originalSupplierSku.toLowerCase() === key
            && r.supplierSku.toLowerCase() === key,
        );
        return !stillSelf;
      }
      return true;
    })
    : uniqueSkus;

  // 关联属性规则与商家 SKU 查重互不依赖，并行请求
  const [linkedResult, skuRepeatResult] = await Promise.all([
    needLinkedRules
      ? (async () => {
        try {
          const rulesRaw = await sheinPlatformAPI.getAssociatedAttributeRules(shopId, {
            get_linked_rule_req_list: [{
              group_id: '1',
              category_id: categoryId,
              product_type_id: productTypeId,
              attribute_list: attributeList,
            }],
          });
          return validateAssociatedRulesRaw(form, rulesRaw, attrTpl);
        } catch (ex) {
          return fail(ex?.message || '查询关联属性规则失败，请稍后重试', 'attrs', 'productAttributes');
        }
      })()
      : Promise.resolve({ ok: true }),
    toCheck.length
      ? (async () => {
        try {
          const checkRaw = await sheinPlatformAPI.checkSupplierSkuRepeated(shopId, toCheck);
          const info = unwrapInfo(checkRaw);
          const list = Array.isArray(info) ? info : (info?.list || info?.data || []);
          const repeated = (Array.isArray(list) ? list : [])
            .filter((row) => row && (row.repeated === true || row.repeated === 'true'))
            .map((row) => String(row.supplierSku || row.supplier_sku || '').trim())
            .filter(Boolean);
          if (repeated.length) {
            return fail(
              `商家 SKU 在店铺内已存在，请更换：${repeated.slice(0, 8).join('、')}${repeated.length > 8 ? '…' : ''}`,
              'spec',
              'supply',
            );
          }
          return { ok: true };
        } catch (ex) {
          return fail(ex?.message || '校验商家 SKU 唯一性失败，请稍后重试', 'spec', 'supply');
        }
      })()
      : Promise.resolve({ ok: true }),
  ]);

  if (!linkedResult.ok) return linkedResult;
  if (!skuRepeatResult.ok) return skuRepeatResult;

  return { ok: true, standardRaw, siteList: resolvedSiteList, currency: currencyCheck };
}
